import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault, getRecipient, createRecipient, updateRecipient, inviteRecipient } from '../lib/vault/vaultApi';
import { ApiError } from '../lib/apiClient';
import './auth.css';

type Mode = 'real' | 'placeholder';

export default function RecipientFormScreen() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = typeof id === 'string' && id.length > 0;

  const [vaultId, setVaultId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('real');
  const [fullName, setFullName] = useState('');
  const [label, setLabel] = useState('');
  const [relationship, setRelationship] = useState('');
  const [dob, setDob] = useState('');
  const [email, setEmail] = useState('');
  const [persistedEmail, setPersistedEmail] = useState('');
  const [phone, setPhone] = useState('');
  // "How we'll find them" — extra reachability channels. At least TWO ways to
  // reach a real recipient are required (the second person who knows them is the
  // one that survives decades, when every address and number has changed).
  const [altEmail, setAltEmail] = useState('');
  const [postalAddress, setPostalAddress] = useState('');
  const [secondName, setSecondName] = useState('');
  const [secondDetail, setSecondDetail] = useState('');
  // A minor's messages are delivered to a parent/guardian, never to the child.
  const [isMinor, setIsMinor] = useState(false);
  const [guardianEmail, setGuardianEmail] = useState('');
  const [hasGuardianEmail, setHasGuardianEmail] = useState(false);
  // How a minor's messages are released. Default: escort to the guardian now +
  // guaranteed direct delivery to the child at 18. Alternative: sealed until 18
  // (no one, not even the guardian, sees it early). Either way the child is
  // guaranteed to receive it at majority — no one can permanently withhold it.
  const [minorReleaseMode, setMinorReleaseMode] = useState<
    'guardian_then_child_at_majority' | 'sealed_until_majority'
  >('guardian_then_child_at_majority');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const dash = await getMyVault(api);
        setVaultId(dash.vault.id);
        if (editing) {
          const { recipient } = await getRecipient(api, dash.vault.id, id as string);
          setMode(recipient.is_placeholder ? 'placeholder' : 'real');
          setFullName(recipient.full_name);
          setLabel(recipient.placeholder_label ?? '');
          setRelationship(recipient.relationship);
          setDob(recipient.date_of_birth ?? '');
          setEmail(recipient.email ?? '');
          setPersistedEmail(recipient.email ?? '');
          setPhone(recipient.phone ?? '');
          setAltEmail(recipient.alt_email ?? '');
          setPostalAddress(recipient.postal_address ?? '');
          setSecondName(recipient.secondary_contact_name ?? '');
          setSecondDetail(recipient.secondary_contact_detail ?? '');
          setIsMinor(recipient.is_minor === true);
          setHasGuardianEmail(recipient.has_minor_guardian_email === true);
          if (
            recipient.minor_release_mode === 'sealed_until_majority' ||
            recipient.minor_release_mode === 'guardian_then_child_at_majority'
          ) {
            setMinorReleaseMode(recipient.minor_release_mode);
          }
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          navigate('/'); // no vault, or recipient gone — back to the dashboard
        } else {
          setError(err instanceof Error ? err.message : 'Could not load this recipient.');
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (vaultId === null) return;
    setBusy(true); setError('');
    const normalizedPhone = phone.replace(/[\s()\-]/g, '');
    const guardian = guardianEmail.trim();
    const alt = altEmail.trim();
    const postal = postalAddress.trim();
    const secName = secondName.trim();
    const secDetail = secondDetail.trim();
    try {
      if (editing) {
        await updateRecipient(api, vaultId, id as string, {
          full_name: fullName, relationship,
          ...(mode === 'real' ? {
            date_of_birth: dob, email: email || null, phone: normalizedPhone || null,
            is_minor: isMinor,
            // Reachability channels: null clears, a value sets. The second
            // contact's name + detail always move together.
            alt_email: alt || null,
            postal_address: postal || null,
            secondary_contact_name: secName || null,
            secondary_contact_detail: secDetail || null,
            // Only send the guardian email when the member actually typed one, so
            // editing other fields never wipes a stored guardian email (its value
            // is never returned to prefill).
            ...(isMinor && guardian ? { minor_guardian_email: guardian } : {}),
            ...(isMinor ? { minor_release_mode: minorReleaseMode } : {}),
          } : {}),
        });
      } else if (mode === 'placeholder') {
        await createRecipient(api, vaultId, { is_placeholder: true, placeholder_label: label, relationship });
      } else {
        await createRecipient(api, vaultId, {
          full_name: fullName, relationship, date_of_birth: dob,
          ...(email ? { email } : {}), ...(normalizedPhone ? { phone: normalizedPhone } : {}),
          ...(alt ? { alt_email: alt } : {}),
          ...(postal ? { postal_address: postal } : {}),
          ...(secName ? { secondary_contact_name: secName } : {}),
          ...(secDetail ? { secondary_contact_detail: secDetail } : {}),
          ...(isMinor ? { is_minor: true, minor_release_mode: minorReleaseMode } : {}),
          ...(isMinor && guardian ? { minor_guardian_email: guardian } : {}),
        });
      }
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this recipient.');
    } finally { setBusy(false); }
  }

  async function onInvite() {
    if (vaultId === null || !editing) return;
    setError(''); setNotice(''); setInviting(true);
    try {
      await inviteRecipient(api, vaultId, id as string);
      setNotice('An invitation is on its way.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the invitation.');
    } finally { setInviting(false); }
  }

  return (
    <section className="auth">
      <div className="auth__card">
        <p className="auth__kicker">{editing ? 'Edit recipient' : 'Add a recipient'}</p>
        <h1 className="auth__title">{editing ? 'Update their details.' : 'Who is this for?'}</h1>
        <p className="auth__alert" role="alert">{error}</p>
        {notice && <p className="auth__notice" role="status">{notice}</p>}

        {!editing && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button type="button" className="la-btn-secondary" aria-pressed={mode === 'real'} onClick={() => setMode('real')}>Someone real</button>
            <button type="button" className="la-btn-secondary" aria-pressed={mode === 'placeholder'} onClick={() => setMode('placeholder')}>Placeholder</button>
          </div>
        )}

        <form onSubmit={onSubmit}>
          {!editing && mode === 'placeholder' ? (
            <label className="auth__field">
              <span className="auth__label">Label</span>
              <input className="auth__input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="My future grandchild" required />
            </label>
          ) : (
            <label className="auth__field">
              <span className="auth__label">Full name</span>
              <input className="auth__input" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
            </label>
          )}
          <label className="auth__field">
            <span className="auth__label">Relationship</span>
            <input className="auth__input" value={relationship} onChange={(e) => setRelationship(e.target.value)} required />
          </label>
          {mode === 'real' && (
            <>
              <label className="auth__field">
                <span className="auth__label">Date of birth</span>
                <input className="auth__input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
              </label>
              <label className="auth__check">
                <input type="checkbox" checked={isMinor} onChange={(e) => setIsMinor(e.target.checked)} />
                <span>This is a child — deliver their messages to a parent or guardian</span>
              </label>
              {isMinor ? (
                <label className="auth__field">
                  <span className="auth__label">Parent or guardian email</span>
                  <input className="auth__input" type="email" value={guardianEmail} placeholder={hasGuardianEmail ? '•••••• (a guardian email is saved)' : ''} onChange={(e) => setGuardianEmail(e.target.value)} />
                  <span className="auth__hint">While {fullName || 'they'} are under 18, their messages are sent here — the person who receives on their behalf. No confirmation code is needed.</span>
                </label>
              ) : null}
              {isMinor ? (
                <fieldset className="auth__field" style={{ border: 0, padding: 0, margin: 0 }}>
                  <span className="auth__label">Until {fullName || 'they'} turn 18</span>
                  <label className="auth__check">
                    <input
                      type="radio"
                      name="minorReleaseMode"
                      checked={minorReleaseMode === 'guardian_then_child_at_majority'}
                      onChange={() => setMinorReleaseMode('guardian_then_child_at_majority')}
                    />
                    <span>Send to their guardian now — and also deliver it to {fullName || 'them'} directly at 18</span>
                  </label>
                  <label className="auth__check">
                    <input
                      type="radio"
                      name="minorReleaseMode"
                      checked={minorReleaseMode === 'sealed_until_majority'}
                      onChange={() => setMinorReleaseMode('sealed_until_majority')}
                    />
                    <span>Keep it sealed until 18 — no one, not even their guardian, sees it before then</span>
                  </label>
                  <span className="auth__hint">
                    Either way, {fullName || 'they'} are guaranteed to receive it at 18, on their own — no one can take it from them.
                  </span>
                </fieldset>
              ) : null}
              {!isMinor ? (
                <>
                  <label className="auth__field">
                    <span className="auth__label">Email</span>
                    <input className="auth__input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </label>
                  <label className="auth__field">
                    <span className="auth__label">Phone</span>
                    <input className="auth__input" type="tel" inputMode="tel" placeholder="+15551234567" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </label>
                </>
              ) : null}

              {/* "How we'll find them" — the reachability record. People move,
                  emails die; a second person who knows them is the channel that
                  lasts. We ask for at least two ways so there is always a thread
                  to pick up, and never use them for anything else. */}
              <fieldset className="auth__group">
                <legend className="auth__label">How we'll find them</legend>
                <p className="auth__hint">
                  Give at least two ways to reach {fullName || 'them'} when the time comes. We only
                  ever use these to deliver what you've left — never for anything else.
                </p>
                <label className="auth__field">
                  <span className="auth__label">Another email</span>
                  <input className="auth__input" type="email" value={altEmail} onChange={(e) => setAltEmail(e.target.value)} placeholder="A backup address" />
                </label>
                <label className="auth__field">
                  <span className="auth__label">Postal address</span>
                  <input className="auth__input" value={postalAddress} onChange={(e) => setPostalAddress(e.target.value)} placeholder="Where a letter would reach them" />
                </label>
                <label className="auth__field">
                  <span className="auth__label">Someone who would know them</span>
                  <input className="auth__input" value={secondName} onChange={(e) => setSecondName(e.target.value)} placeholder="Their name" />
                </label>
                <label className="auth__field">
                  <span className="auth__label">…and how to reach that person</span>
                  <input className="auth__input" value={secondDetail} onChange={(e) => setSecondDetail(e.target.value)} placeholder="Email, phone, or address" />
                  <span className="auth__hint">The most durable of all — long after every address has changed, someone in their life still knows where they are.</span>
                </label>
              </fieldset>
            </>
          )}
          <div className="la-cta-wrap">
            <button className="la-btn-primary" type="submit" disabled={busy || vaultId === null}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </form>

        {editing && mode === 'real' && !isMinor && persistedEmail && (
          <button type="button" className="la-btn-secondary" style={{ marginTop: 12 }} onClick={onInvite} disabled={inviting}>Invite them to enrol</button>
        )}
        <p className="auth__alt"><button type="button" className="la-btn-secondary" onClick={() => navigate('/')}>Back to vault</button></p>
      </div>
    </section>
  );
}
